/**
 * Settings Page - MaintainX Style
 * Clean settings layout with sections for Notifications, Integrations, Account
 */

import { useState, useEffect } from "react";
import { API, useAuth } from "@/App";
import {
  Bell,
  MessageSquare,
  Link as LinkIcon,
  User,
  Shield,
  Building2,
  CheckCircle,
  AlertCircle,
  ExternalLink,
  Copy,
  RefreshCw,
  ChevronRight,
  Smartphone,
  Mail,
  Slack,
  Send
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

// Settings Section Component
const SettingsSection = ({ title, description, children }) => (
  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
    <div className="px-6 py-4 border-b border-slate-100">
      <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
      {description && <p className="text-sm text-slate-500 mt-1">{description}</p>}
    </div>
    <div className="p-6">{children}</div>
  </div>
);

// Settings Item Component
const SettingsItem = ({ icon: Icon, title, description, action, status }) => (
  <div className="flex items-center justify-between py-4 border-b border-slate-100 last:border-0">
    <div className="flex items-center gap-4">
      <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center text-slate-600">
        <Icon size={20} />
      </div>
      <div>
        <h4 className="font-medium text-slate-900">{title}</h4>
        <p className="text-sm text-slate-500">{description}</p>
      </div>
    </div>
    <div className="flex items-center gap-3">
      {status}
      {action}
    </div>
  </div>
);

// Connection Status Badge
const ConnectionStatus = ({ connected, label }) => (
  <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
    connected 
      ? "bg-emerald-100 text-emerald-700" 
      : "bg-slate-100 text-slate-600"
  }`}>
    {connected ? <CheckCircle size={14} /> : <AlertCircle size={14} />}
    {label || (connected ? "Connected" : "Not Connected")}
  </span>
);

// Telegram Setup Component
const TelegramSetup = () => {
  const { token, user } = useAuth();
  const [telegramConnected, setTelegramConnected] = useState(false);
  const [telegramUsername, setTelegramUsername] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");

  useEffect(() => {
    checkTelegramStatus();
  }, []);

  const checkTelegramStatus = async () => {
    try {
      const response = await fetch(`${API}/telegram/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTelegramConnected(data.connected);
        setTelegramUsername(data.username || "");
      }
    } catch (error) {
      console.error("Failed to check Telegram status:", error);
    }
  };

  const generateVerificationCode = async () => {
    setConnecting(true);
    try {
      const response = await fetch(`${API}/telegram/generate-code`, {
        method: "POST",
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });
      if (response.ok) {
        const data = await response.json();
        setVerificationCode(data.code);
        toast.success("Verification code generated!");
      }
    } catch (error) {
      toast.error("Failed to generate code");
    } finally {
      setConnecting(false);
    }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(verificationCode);
    toast.success("Code copied to clipboard!");
  };

  const disconnectTelegram = async () => {
    try {
      const response = await fetch(`${API}/telegram/disconnect`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        setTelegramConnected(false);
        setTelegramUsername("");
        setVerificationCode("");
        toast.success("Telegram disconnected");
      }
    } catch (error) {
      toast.error("Failed to disconnect");
    }
  };

  if (telegramConnected) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-lg border border-emerald-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
              <CheckCircle className="text-emerald-600" size={20} />
            </div>
            <div>
              <p className="font-medium text-emerald-900">Telegram Connected</p>
              <p className="text-sm text-emerald-700">@{telegramUsername || "your_account"}</p>
            </div>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={disconnectTelegram}
            className="text-slate-600 hover:text-red-600 hover:border-red-300"
          >
            Disconnect
          </Button>
        </div>
        <p className="text-sm text-slate-500">
          You'll receive instant notifications for maintenance requests, order updates, and critical alerts.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <Send className="text-blue-600 mt-0.5" size={20} />
        <div>
          <p className="font-medium text-blue-900">Get instant notifications on Telegram</p>
          <p className="text-sm text-blue-700 mt-1">
            Receive real-time alerts for new maintenance requests, order updates, and critical system notifications directly on your phone.
          </p>
        </div>
      </div>

      {!verificationCode ? (
        <div className="space-y-4">
          <h4 className="font-medium text-slate-900">How to connect:</h4>
          <ol className="space-y-3 text-sm text-slate-600">
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 bg-slate-200 rounded-full flex items-center justify-center text-xs font-medium text-slate-700 flex-shrink-0">1</span>
              <span>Click the button below to generate your verification code</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 bg-slate-200 rounded-full flex items-center justify-center text-xs font-medium text-slate-700 flex-shrink-0">2</span>
              <span>Open Telegram and search for <strong>@AskIsaacBot</strong></span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 bg-slate-200 rounded-full flex items-center justify-center text-xs font-medium text-slate-700 flex-shrink-0">3</span>
              <span>Send your verification code to the bot</span>
            </li>
          </ol>
          <Button 
            onClick={generateVerificationCode}
            disabled={connecting}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {connecting ? (
              <>
                <RefreshCw size={16} className="mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Send size={16} className="mr-2" />
                Generate Verification Code
              </>
            )}
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
            <p className="text-sm text-slate-600 mb-2">Your verification code:</p>
            <div className="flex items-center gap-3">
              <code className="flex-1 px-4 py-3 bg-white rounded-lg border border-slate-300 font-mono text-lg text-slate-900">
                {verificationCode}
              </code>
              <Button variant="outline" size="sm" onClick={copyCode}>
                <Copy size={16} className="mr-2" />
                Copy
              </Button>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              variant="outline"
              onClick={() => window.open("https://t.me/AskIsaacBot", "_blank")}
            >
              <ExternalLink size={16} className="mr-2" />
              Open Telegram Bot
            </Button>
            <Button 
              variant="ghost"
              onClick={checkTelegramStatus}
              className="text-slate-600"
            >
              <RefreshCw size={16} className="mr-2" />
              Check Status
            </Button>
          </div>
          <p className="text-xs text-slate-500">
            Code expires in 10 minutes. Send this code to @AskIsaacBot on Telegram to complete setup.
          </p>
        </div>
      )}
    </div>
  );
};

// Main Settings Page
export default function SettingsPage() {
  const { user } = useAuth();
  const [activeSection, setActiveSection] = useState("notifications");

  const sections = [
    { id: "notifications", label: "Notifications", icon: Bell },
    { id: "integrations", label: "Integrations", icon: LinkIcon },
    { id: "account", label: "Account", icon: User },
    { id: "security", label: "Security", icon: Shield },
  ];

  return (
    <div className="max-w-5xl mx-auto">
      {/* Section Tabs */}
      <div className="flex gap-2 mb-6 border-b border-slate-200 pb-4">
        {sections.map((section) => {
          const Icon = section.icon;
          return (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeSection === section.id
                  ? "bg-blue-50 text-blue-700"
                  : "text-slate-600 hover:bg-slate-100"
              }`}
            >
              <Icon size={18} />
              {section.label}
            </button>
          );
        })}
      </div>

      {/* Notifications Section */}
      {activeSection === "notifications" && (
        <div className="space-y-6">
          <SettingsSection 
            title="Telegram Notifications" 
            description="Connect Telegram for instant mobile alerts"
          >
            <TelegramSetup />
          </SettingsSection>

          <SettingsSection 
            title="Email Notifications" 
            description="Manage which emails you receive"
          >
            <div className="space-y-4">
              <SettingsItem
                icon={Mail}
                title="Maintenance Alerts"
                description="Get notified when new maintenance requests are submitted"
                status={<ConnectionStatus connected={true} label="Enabled" />}
                action={
                  <Button variant="ghost" size="sm">
                    <ChevronRight size={16} />
                  </Button>
                }
              />
              <SettingsItem
                icon={Mail}
                title="Order Updates"
                description="Receive updates on order status changes"
                status={<ConnectionStatus connected={true} label="Enabled" />}
                action={
                  <Button variant="ghost" size="sm">
                    <ChevronRight size={16} />
                  </Button>
                }
              />
              <SettingsItem
                icon={Mail}
                title="Weekly Reports"
                description="Summary of weekly activity and metrics"
                status={<ConnectionStatus connected={false} label="Disabled" />}
                action={
                  <Button variant="ghost" size="sm">
                    <ChevronRight size={16} />
                  </Button>
                }
              />
            </div>
          </SettingsSection>
        </div>
      )}

      {/* Integrations Section */}
      {activeSection === "integrations" && (
        <div className="space-y-6">
          <SettingsSection 
            title="Connected Services" 
            description="Manage your third-party integrations"
          >
            <div className="space-y-4">
              <SettingsItem
                icon={Send}
                title="Telegram"
                description="Instant notifications via Telegram messenger"
                status={<ConnectionStatus connected={false} />}
                action={
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setActiveSection("notifications")}
                  >
                    Setup
                  </Button>
                }
              />
              <SettingsItem
                icon={Building2}
                title="QuickBooks"
                description="Sync invoices and financial data"
                status={<ConnectionStatus connected={false} />}
                action={
                  <Button variant="outline" size="sm">
                    Connect
                  </Button>
                }
              />
              <SettingsItem
                icon={Slack}
                title="Slack"
                description="Team notifications in Slack channels"
                status={<ConnectionStatus connected={false} />}
                action={
                  <Button variant="outline" size="sm" disabled>
                    Coming Soon
                  </Button>
                }
              />
            </div>
          </SettingsSection>

          <SettingsSection 
            title="API Access" 
            description="Manage API keys for custom integrations"
          >
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 text-center">
              <p className="text-sm text-slate-600">
                API access is available on Enterprise plans.
              </p>
              <Button variant="link" className="text-blue-600 mt-2">
                Learn more about Enterprise
              </Button>
            </div>
          </SettingsSection>
        </div>
      )}

      {/* Account Section */}
      {activeSection === "account" && (
        <div className="space-y-6">
          <SettingsSection 
            title="Profile Information" 
            description="Your personal account details"
          >
            <div className="space-y-4">
              <div className="flex items-center gap-4 pb-4 border-b border-slate-100">
                <div className="w-16 h-16 bg-slate-200 rounded-full flex items-center justify-center">
                  <User size={28} className="text-slate-500" />
                </div>
                <div>
                  <h4 className="font-medium text-slate-900">{user?.full_name || user?.name || "Admin"}</h4>
                  <p className="text-sm text-slate-500">{user?.email}</p>
                  <p className="text-xs text-slate-400 mt-1 capitalize">{user?.role?.replace("_", " ")}</p>
                </div>
                <Button variant="outline" size="sm" className="ml-auto">
                  Edit Profile
                </Button>
              </div>
              <SettingsItem
                icon={Building2}
                title="Organization"
                description={user?.hospital_name || "Sterile Mate"}
                action={
                  <Button variant="ghost" size="sm">
                    <ChevronRight size={16} />
                  </Button>
                }
              />
            </div>
          </SettingsSection>
        </div>
      )}

      {/* Security Section */}
      {activeSection === "security" && (
        <div className="space-y-6">
          <SettingsSection 
            title="Security Settings" 
            description="Manage your account security"
          >
            <div className="space-y-4">
              <SettingsItem
                icon={Shield}
                title="Password"
                description="Last changed 30 days ago"
                action={
                  <Button variant="outline" size="sm">
                    Change Password
                  </Button>
                }
              />
              <SettingsItem
                icon={Smartphone}
                title="Two-Factor Authentication"
                description="Add an extra layer of security"
                status={<ConnectionStatus connected={false} label="Not Enabled" />}
                action={
                  <Button variant="outline" size="sm">
                    Enable
                  </Button>
                }
              />
            </div>
          </SettingsSection>

          <SettingsSection 
            title="Active Sessions" 
            description="Manage devices where you're logged in"
          >
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                    <CheckCircle size={16} className="text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900">Current Session</p>
                    <p className="text-xs text-slate-500">Chrome on Windows • Active now</p>
                  </div>
                </div>
                <span className="text-xs text-emerald-600 font-medium">This device</span>
              </div>
            </div>
          </SettingsSection>
        </div>
      )}
    </div>
  );
}
